import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { isValid, parse } from 'date-fns'
import { isEmpty, isNil } from 'lodash-es'
import styled from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { getProgramNameFromId } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Checkbox } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { DateField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/DateField.web'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { type ClientRecordsPdfExport, onExportClientRecords, openClientRecordsPdf } from './onExportClientRecords'
import {
  hasRecordsPdfAdHocTerm,
  mergeRecordsPdfRedactionTerms,
  normalizeRecordsPdfAdHocTerm,
} from './recordsPdfAdHocTerms'
import { getNameInitials } from './recordsPdfBlob'
import { RecordsPdfRedactionOptions } from './RecordsPdfRedactionOptions'
import {
  getRecordsPdfRedactionKeywords,
  RECORDS_PDF_REDACTION_CATEGORIES,
  RECORDS_PDF_REDACTION_RULES,
} from './recordsPdfRedactionRules'
import { type RecordsPdfAssignmentsSession } from './recordsPdfSections'
import { RecordsPdfSourceComponents } from './RecordsPdfSourceComponents'
import { SUPERVISOR_ROLES } from '../../../../common/constants/appConstants'
import { getEpisodeDropdownItemText, hasRole } from '../../../../common/utils/utils'
import { getAuthConfig, getAuthSupervisor, getAuthUserId } from '../../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../../data/store'
import { useIsEngageDAProvider } from '../../../../hooks/useIsEngageDAProvider'
import { getProviderDetailsData } from '../../../../providers/data/providerSelectors'
import { setToastContent } from '../../../data/ltToastAutoActions'
import { useAllEpisodesForSelectedClient } from '../../../episodes/data/hooks/useAllEpisodesForSelectedClient'
import { useSelectedEpisodeForClient } from '../../../episodes/data/hooks/useSelectedEpisodeForClient'
import { useSelectedClientData } from '../data/hooks/useSelectedClientData'

const RecordsPdfRedactionViewer = lazy(() => import('./RecordsPdfRedactionViewer'))

const ButtonsContainer = styled.View<{ $redactionEnabled: boolean }>(({ $redactionEnabled, theme: { spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: spacing['16px'],
  ...($redactionEnabled && { marginBottom: spacing['32px'] }),
}))

const LegacySectionsExportSelectionContainer = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
}))

const LegacyOptionsContainer = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['32px'],
}))

const SectionsExportSelectionColumn = styled.View(({ theme: { breakpoints } }) => ({
  minWidth: 0,
  ...(!breakpoints.isMobileSized && {
    flex: 1,
    flexBasis: 0,
  }),
}))

const SectionsExportSelectionContainer = styled.View(({ theme: { colors, spacing } }) => ({
  gap: spacing['24px'],
  minWidth: 0,
  padding: spacing['24px'],
  border: `1px solid ${colors.borderDefault}`,
  borderRadius: 12,
  boxSizing: 'border-box',
}))

const CardHeader = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['8px'],
}))

const SectionsList = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
}))

const ExportOptionsRow = styled.View(({ theme: { breakpoints, spacing } }) => ({
  alignItems: 'stretch',
  flexDirection: breakpoints.isMobileSized ? 'column' : 'row',
  gap: spacing['24px'],
}))

const DateRangeContainer = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
}))

const DateFieldContainer = styled.View<{ $redactionEnabled: boolean }>(
  ({ $redactionEnabled, theme: { breakpoints, spacing } }) => ({
    display: 'flex',
    flexDirection: $redactionEnabled && breakpoints.isMobileSized ? 'column' : 'row',
    gap: $redactionEnabled ? spacing['24px'] : spacing['48px'],
    marginLeft: spacing['32px'],
  }),
)

const ExportToIndexedDBContainer = styled.View({
  opacity: 0,
  marginTop: 100,
})

const PdfReviewLoadingContainer = styled.View(({ theme: { colors } }) => ({
  alignItems: 'center',
  backgroundColor: colors.backgroundSecondary,
  bottom: 0,
  justifyContent: 'center',
  left: 0,
  position: 'fixed',
  right: 0,
  top: 0,
  zIndex: 10000,
}))

const PdfReviewLoadingFallback = () => (
  <PdfReviewLoadingContainer>
    <LoadingIndicator size={45} topPadding={0} />
  </PdfReviewLoadingContainer>
)

const ClientRecords: React.FC = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { isExportRedactionEnabled = false } = useFlags()

  const [isExportingPDF, setIsExportingPDF] = useState(false)
  const [pdfExportForReview, setPdfExportForReview] = useState<ClientRecordsPdfExport>()
  const [automaticRedactionsEnabled, setAutomaticRedactionsEnabled] = useState(false)
  const [selectedRedactionRuleIds, setSelectedRedactionRuleIds] = useState<ReadonlySet<string>>(
    () => new Set(RECORDS_PDF_REDACTION_RULES.map(({ id }) => id)),
  )
  const [enabledRedactionCategoryIds, setEnabledRedactionCategoryIds] = useState<ReadonlySet<string>>(
    () => new Set(RECORDS_PDF_REDACTION_CATEGORIES.map(({ id }) => id)),
  )
  const [expandedRedactionCategoryIds, setExpandedRedactionCategoryIds] = useState<ReadonlySet<string>>(() => new Set())
  const [adHocRedactionTerms, setAdHocRedactionTerms] = useState<readonly string[]>([])

  const [allSections, setAllSections] = useState(true)
  const [benefitsSummarySection, setBenefitsSummarySection] = useState(false)
  const [sessionsSection, setSessionsSection] = useState(false)
  const [notesSection, setNotesSection] = useState(false)
  const [progressSection, setProgressSection] = useState(false)
  const [assignmentsSection, setAssignmentsSection] = useState(false)
  const [medicationsSection, setMedicationsSection] = useState(false)

  const [showDateSelection, setShowDateSelection] = useState(false)
  const [selectedStartDate, setSelectedStartDate] = useState<string | undefined>(undefined)
  const [selectedEndDate, setSelectedEndDate] = useState<string | undefined>(undefined)
  const [isStartDateInputValid, setIsStartDateInputValid] = useState<boolean>(true)
  const [isEndDateInputValid, setIsEndDateInputValid] = useState<boolean>(true)

  const [exportToIndexedDB, setExportToIndexedDB] = useState(false)

  const [clientSummaryPDFContent, setClientSummaryPDFContent] = useState<any>(null)
  const [sessionsPDFContent, setSessionsPDFContent] = useState<any>(null)
  const [notesPDFContent, setNotesPDFContent] = useState<{ [key: string]: React.ReactNode | undefined } | undefined>(
    undefined,
  )
  const [clientProgressPDFContent, setClientProgressPDFContent] = useState<any>(null)
  const [clientAssignmentsPDFContent, setClientAssignmentsPDFContent] = useState<
    | {
        episodeName: string
        assignmentsBySession: RecordsPdfAssignmentsSession[]
      }[]
    | undefined
    | null
  >(null)
  const [medicationsPDFContent, setMedicationsPDFContent] = useState<any>(null)

  useEffect(() => {
    if (!isExportRedactionEnabled) {
      setPdfExportForReview(undefined)
    }
  }, [isExportRedactionEnabled])

  const redactionKeywordsForReview = useMemo(() => {
    if (!automaticRedactionsEnabled) return []

    const activeRuleIds = new Set<string>()
    RECORDS_PDF_REDACTION_CATEGORIES.forEach((category) => {
      if (!enabledRedactionCategoryIds.has(category.id)) return

      category.rules.forEach(({ id }) => {
        if (selectedRedactionRuleIds.has(id)) activeRuleIds.add(id)
      })
    })
    return mergeRecordsPdfRedactionTerms(getRecordsPdfRedactionKeywords(activeRuleIds), adHocRedactionTerms)
  }, [adHocRedactionTerms, automaticRedactionsEnabled, enabledRedactionCategoryIds, selectedRedactionRuleIds])

  const handleAddAdHocRedactionTerm = useCallback(
    (term: string) => {
      if (!automaticRedactionsEnabled) return

      const normalizedTerm = normalizeRecordsPdfAdHocTerm(term)
      if (!normalizedTerm) return

      setAdHocRedactionTerms((terms) =>
        hasRecordsPdfAdHocTerm(terms, normalizedTerm) ? terms : [...terms, normalizedTerm],
      )
    },
    [automaticRedactionsEnabled],
  )

  const handleRemoveAdHocRedactionTerm = useCallback((termToRemove: string) => {
    setAdHocRedactionTerms((terms) => terms.filter((term) => term !== termToRemove))
  }, [])

  const handleRedactionCategoryToggle = useCallback((categoryId: string) => {
    setEnabledRedactionCategoryIds((enabledCategoryIds) => {
      const nextEnabledCategoryIds = new Set(enabledCategoryIds)
      if (nextEnabledCategoryIds.has(categoryId)) {
        nextEnabledCategoryIds.delete(categoryId)
      } else {
        nextEnabledCategoryIds.add(categoryId)
      }
      return nextEnabledCategoryIds
    })
  }, [])

  const handleRedactionRuleToggle = useCallback((ruleId: string) => {
    setSelectedRedactionRuleIds((selectedRuleIds) => {
      const nextSelectedRuleIds = new Set(selectedRuleIds)
      if (nextSelectedRuleIds.has(ruleId)) {
        nextSelectedRuleIds.delete(ruleId)
      } else {
        nextSelectedRuleIds.add(ruleId)
      }
      return nextSelectedRuleIds
    })
  }, [])

  const handleRedactionCategoryExpansionToggle = useCallback((categoryId: string) => {
    setExpandedRedactionCategoryIds((expandedCategoryIds) => {
      const nextExpandedCategoryIds = new Set(expandedCategoryIds)
      if (nextExpandedCategoryIds.has(categoryId)) {
        nextExpandedCategoryIds.delete(categoryId)
      } else {
        nextExpandedCategoryIds.add(categoryId)
      }
      return nextExpandedCategoryIds
    })
  }, [])

  const supervisor = useSelector(getAuthSupervisor)
  const isSupervisor = !!supervisor && !isEmpty(supervisor.roles) && hasRole(supervisor.roles, SUPERVISOR_ROLES)
  const { data: clientDetails } = useSelectedClientData()
  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const providerDetailsData = useSelector(getProviderDetailsData)
  const userId = useSelector(getAuthUserId)
  const config = useSelector(getAuthConfig)
  const isDAProvider = useIsEngageDAProvider()
  const { data: episodes = [], isSuccess: fetchedEpisodes } = useAllEpisodesForSelectedClient()

  const providerEpisodesInfoPDF = useMemo(() => {
    return episodes
      .filter((episode: Episode) => episode.provider_id === userId)
      .map((episode: Episode) => {
        return {
          text: getEpisodeDropdownItemText(intl, episode),
          id: episode.id,
        }
      })
  }, [episodes, intl, userId])

  const programName = getProgramNameFromId(selectedEpisode?.program_id) ?? ''

  const exportPDF = useCallback(async () => {
    if (!clientDetails) {
      return
    }

    setIsExportingPDF(true)

    try {
      const pdfExport = await onExportClientRecords({
        clientDetails,
        intl,
        providerId: userId,
        providerName:
          [providerDetailsData?.first_name, providerDetailsData?.last_name].filter(Boolean).join(' ') || undefined,
        providerInitials: getNameInitials(providerDetailsData?.first_name, providerDetailsData?.last_name),
        programName,
        allSections,
        benefitsSummarySection,
        sessionsSection,
        notesSection,
        progressSection,
        assignmentsSection,
        medicationsSection,
        clientSummaryPDFContent,
        sessionsPDFContent,
        notesPDFContent,
        clientProgressPDFContent,
        clientAssignmentsPDFContent,
        medicationsPDFContent,
        exportToIndexedDB,
        isEngageDAProvider: isDAProvider,
      })
      setIsExportingPDF(false)
      if (isExportRedactionEnabled) {
        setPdfExportForReview(pdfExport)
      } else {
        if (!exportToIndexedDB) {
          openClientRecordsPdf(pdfExport)
        }
        navigate(-1)
      }
    } catch {
      setIsExportingPDF(false)
      dispatch(
        setToastContent({
          text: formatMessage({
            defaultMessage: 'Failed to export PDF - please try again later.',
            description: 'A toast message letting the user know that the client records pdf failed to export',
          }),
          toastType: 'error',
          id: 'ClientRecords-exportPDF-error',
        }),
      )
    }
  }, [
    clientDetails,
    clientSummaryPDFContent,
    clientProgressPDFContent,
    clientAssignmentsPDFContent,
    exportToIndexedDB,
    medicationsPDFContent,
    dispatch,
    formatMessage,
    notesPDFContent,
    programName,
    providerDetailsData?.first_name,
    providerDetailsData?.last_name,
    sessionsPDFContent,
    userId,
    allSections,
    benefitsSummarySection,
    sessionsSection,
    notesSection,
    progressSection,
    assignmentsSection,
    medicationsSection,
    isDAProvider,
    isExportRedactionEnabled,
    intl,
    navigate,
  ])

  const handleStartDateChange = ({ inputString, dateFormat }: { inputString: string; dateFormat: string }) => {
    const parsed = parse(inputString, dateFormat, new Date())
    const isValidDate = isValid(parsed) && inputString.length === dateFormat.length
    setIsStartDateInputValid(isValidDate)
    if (isValidDate) {
      setSelectedStartDate(inputString)
    }
  }

  const handleEndDateChange = ({ inputString, dateFormat }: { inputString: string; dateFormat: string }) => {
    const parsed = parse(inputString, dateFormat, new Date())
    const isValidDate = isValid(parsed) && inputString.length === dateFormat.length
    setIsEndDateInputValid(isValidDate)
    if (isValidDate) {
      setSelectedEndDate(inputString)
    }
  }

  const handleCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const handleClosePdfReview = useCallback(() => {
    setPdfExportForReview(undefined)
  }, [])

  const exportButtonDisabled =
    isExportingPDF ||
    !clientDetails ||
    isNil(clientSummaryPDFContent) ||
    (!isSupervisor && config?.medicationEnabled && isNil(medicationsPDFContent)) ||
    !fetchedEpisodes ||
    isNil(notesPDFContent)

  const sectionCheckboxes = (
    <>
      <Checkbox
        checked={allSections}
        onPress={() => {
          setAllSections(!allSections)
          setSessionsSection(false)
          setNotesSection(false)
          setProgressSection(false)
        }}
        title='All Sections'
        testID={tID('ClientRecords-allSectionsCheckbox')}
        baseInputStyle={{ marginBottom: 0 }}
      />
      <Checkbox
        checked={benefitsSummarySection}
        onPress={() => {
          setBenefitsSummarySection(!benefitsSummarySection)
          setAllSections(false)
        }}
        title='Benefits Summary Section'
        testID={tID('ClientRecords-benefitsSummaryOnlyCheckbox')}
        baseInputStyle={{ marginBottom: 0 }}
      />
      <Checkbox
        checked={sessionsSection}
        onPress={() => {
          setSessionsSection(!sessionsSection)
          setAllSections(false)
        }}
        title='Sessions Section'
        testID={tID('ClientRecords-sessionsOnlyCheckbox')}
        baseInputStyle={{ marginBottom: 0 }}
      />
      <Checkbox
        checked={notesSection}
        onPress={() => {
          setNotesSection(!notesSection)
          setAllSections(false)
        }}
        title='Notes Section'
        testID={tID('ClientRecords-notesOnlyCheckbox')}
        baseInputStyle={{ marginBottom: 0 }}
      />
      <Checkbox
        checked={progressSection}
        onPress={() => {
          setProgressSection(!progressSection)
          setAllSections(false)
        }}
        title='Progress Section'
        testID={tID('ClientRecords-progressOnlyCheckbox')}
        baseInputStyle={{ marginBottom: 0 }}
      />
      <Checkbox
        checked={assignmentsSection}
        onPress={() => {
          setAssignmentsSection(!assignmentsSection)
          setAllSections(false)
        }}
        title='Assignments Section'
        testID={tID('ClientRecords-assignmentsOnlyCheckbox')}
        baseInputStyle={{ marginBottom: 0 }}
      />
      <Checkbox
        checked={medicationsSection}
        onPress={() => {
          setMedicationsSection(!medicationsSection)
          setAllSections(false)
        }}
        title='Medications Section'
        testID={tID('ClientRecords-medicationsOnlyCheckbox')}
        baseInputStyle={{ marginBottom: 0 }}
      />
    </>
  )

  const dateRangeCheckbox = (
    <Checkbox
      checked={showDateSelection}
      onPress={() => {
        if (showDateSelection) {
          setSelectedStartDate(undefined)
          setSelectedEndDate(undefined)
        }
        setShowDateSelection(!showDateSelection)
      }}
      title='Date Range'
      testID={tID('ClientRecords-dateRangeCheckbox')}
      baseInputStyle={{ marginBottom: 0 }}
    />
  )

  const dateFields = showDateSelection ? (
    <DateFieldContainer $redactionEnabled={isExportRedactionEnabled}>
      <DateField
        error={
          isStartDateInputValid
            ? ''
            : formatMessage({
                defaultMessage: 'Date format is invalid',
                description: 'Error message for when provider types the date in in an invalid format',
              })
        }
        label={formatMessage({
          defaultMessage: 'Enter Start Date',
          description: 'Label for field where providers select the start date they are pulling records for',
        })}
        readOnly={false}
        maxDate={new Date()}
        input={{
          name: 'ClientRecords-startDateField',
          onChange: () => {},
          onBlur: () => {},
          onFocus: () => {},
          value: selectedStartDate,
        }}
        appendFormat
        submitError={isStartDateInputValid ? '' : 'Date is in the past'}
        open={false}
        locale={'en-US'}
        onRawChange={handleStartDateChange}
      />
      <DateField
        error={
          isEndDateInputValid
            ? ''
            : formatMessage({
                defaultMessage: 'Date format is invalid',
                description: 'Error message for when provider types the date in in an invalid format',
              })
        }
        label={formatMessage({
          defaultMessage: 'Enter End Date',
          description: 'Label for field where providers select the end date they are pulling records for',
        })}
        readOnly={false}
        maxDate={new Date()}
        input={{
          name: 'ClientRecords-endDateField',
          onChange: () => {},
          onBlur: () => {},
          onFocus: () => {},
          value: selectedEndDate,
        }}
        appendFormat
        submitError={isEndDateInputValid ? '' : 'Date is in the future'}
        open={false}
        locale={'en-US'}
        onRawChange={handleEndDateChange}
      />
    </DateFieldContainer>
  ) : null

  return (
    <>
      <ButtonsContainer $redactionEnabled={isExportRedactionEnabled}>
        {!isExportRedactionEnabled && (
          <SecondaryButton text='Cancel' onPress={handleCancel} testID={tID('ClientRecords-cancelButton')} />
        )}
        <PrimaryButton
          text={
            isExportRedactionEnabled
              ? isExportingPDF
                ? 'Preparing preview...'
                : 'Export Client Records'
              : isExportingPDF
                ? 'Exporting...'
                : 'Export Records'
          }
          onPress={exportPDF}
          loading={isExportingPDF}
          disabled={exportButtonDisabled}
          testID={tID('ClientRecords-exportButton')}
        />
      </ButtonsContainer>

      {isExportRedactionEnabled ? (
        <ExportOptionsRow>
          <SectionsExportSelectionColumn>
            <SectionsExportSelectionContainer>
              <CardHeader>
                <Title
                  size={TitleSize.XSMALL}
                  text={formatMessage({
                    defaultMessage: 'Records to include',
                    description: 'Heading for selecting sections to include in a client records PDF',
                  })}
                  testID={tID('ClientRecords-sectionsTitle')}
                />
                <BodyText
                  size={BodyTextSize.SMALL}
                  text={formatMessage({
                    defaultMessage: 'Choose which sections and date range to include in the PDF.',
                    description: 'Explanation of the client records PDF export section choices',
                  })}
                  testID={tID('ClientRecords-sectionsDescription')}
                />
              </CardHeader>
              <SectionsList>{sectionCheckboxes}</SectionsList>
              <DateRangeContainer>
                {dateRangeCheckbox}
                {dateFields}
              </DateRangeContainer>
            </SectionsExportSelectionContainer>
          </SectionsExportSelectionColumn>
          <RecordsPdfRedactionOptions
            adHocRedactionTerms={adHocRedactionTerms}
            automaticRedactionsEnabled={automaticRedactionsEnabled}
            enabledRedactionCategoryIds={enabledRedactionCategoryIds}
            expandedRedactionCategoryIds={expandedRedactionCategoryIds}
            onAddAdHocRedactionTerm={handleAddAdHocRedactionTerm}
            onAutomaticRedactionsChange={setAutomaticRedactionsEnabled}
            onRedactionCategoryExpansionToggle={handleRedactionCategoryExpansionToggle}
            onRedactionCategoryToggle={handleRedactionCategoryToggle}
            onRedactionRuleToggle={handleRedactionRuleToggle}
            onRemoveAdHocRedactionTerm={handleRemoveAdHocRedactionTerm}
            selectedRedactionRuleIds={selectedRedactionRuleIds}
          />
        </ExportOptionsRow>
      ) : (
        <LegacyOptionsContainer>
          <LegacySectionsExportSelectionContainer>{sectionCheckboxes}</LegacySectionsExportSelectionContainer>
          {dateRangeCheckbox}
          {dateFields}
        </LegacyOptionsContainer>
      )}

      <ExportToIndexedDBContainer>
        <Checkbox
          checked={exportToIndexedDB}
          onPress={() => setExportToIndexedDB(!exportToIndexedDB)}
          title='Export to IndexedDB'
          testID={tID('ClientRecords-exportToIndexedDBCheckbox')}
          name={'exportToIndexedDB'}
        />
      </ExportToIndexedDBContainer>

      <RecordsPdfSourceComponents
        medicationsEnabled={!!config?.medicationEnabled && !isSupervisor}
        pdfContentEndDate={selectedEndDate}
        pdfContentStartDate={selectedStartDate}
        providerEpisodesInfoPDF={providerEpisodesInfoPDF}
        setClientAssignmentsPDFContent={setClientAssignmentsPDFContent}
        setClientProgressPDFContent={setClientProgressPDFContent}
        setClientSummaryPDFContent={setClientSummaryPDFContent}
        setMedicationsPDFContent={setMedicationsPDFContent}
        setNotesPDFContent={setNotesPDFContent}
        setSessionsPDFContent={setSessionsPDFContent}
      />
      {isExportRedactionEnabled && pdfExportForReview && (
        <Suspense fallback={<PdfReviewLoadingFallback />}>
          <RecordsPdfRedactionViewer
            filename={pdfExportForReview.filename}
            pdfBlob={pdfExportForReview.blob}
            redactionExcludedPageNumbers={pdfExportForReview.redactionExcludedPageNumbers}
            redactionKeywords={redactionKeywordsForReview}
            onClose={handleClosePdfReview}
          />
        </Suspense>
      )}
    </>
  )
}

export default ClientRecords
